import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { HouseRules } from "../../lib/api";
import { loadDoor, saveDoorRules } from "../../lib/draft";
import { currentRulesQuery, meQuery, qk } from "../../lib/queries";
import { useSessionExpired } from "../../lib/session";
import { useShellUi } from "../shell/state";
import { useMetered } from "../shell/use-metered";
import { RulesArea } from "./rules-area";

export const RULES_HEADING = "House Rules";
export const RULES_LOADING = "Reading your rules.";
export const RULES_UNREACHABLE = "Nob is not answering. Try that again.";
/** What the doorman is asked when somebody would rather talk than type. */
export const TELL_NOB_ASK = "I want to change my house rules. Ask me what you need to know.";

/**
 * The House Rules, on the door's own page.
 *
 * `/rules` was an address because the document was a destination — the thing
 * you went to look at. In the shell there is nowhere to go: the rules are what
 * the door is made of, so they are on the door's page, under the settings that
 * govern the same object.
 *
 * Everything inside is `RulesArea` exactly as it was, including its five views
 * and its whole write path. What changed is the way OUT: there is one, and it is
 * "tell Nob", which closes the panel and puts the ask in the composer instead of
 * navigating to a screen that is already open. The money and inbox buttons that
 * stood beside it are gone (owner, 2026-08-25) — the inbox is the list standing
 * behind this very panel and the plan is a panel of its own, so both were
 * navigation dressed as an action, in a row where everything else edits the
 * document.
 */
export function RulesPanel() {
  const ui = useShellUi();
  /**
   * Whether this plan does AI work at all. `null` while the read is in flight,
   * and an unanswered read is NOT treated as Free — a paying owner shown a
   * paywall would be the worse of the two mistakes, and the answer is cached.
   */
  const metered = useMetered();
  const queryClient = useQueryClient();
  const sessionExpired = useSessionExpired();
  const { data: me } = useQuery(meQuery());
  const { data: current, isPending, isError } = useQuery(currentRulesQuery());

  const door = loadDoor();
  const seed: HouseRules | null =
    door?.house_rules ??
    (current ? { version: current.version, markdown: current.markdown } : null);

  const onRulesChanged = useCallback(
    (rules: HouseRules) => {
      // The browser's copy of the created door carries the rules it was opened
      // with; it is only rewritten when there is one to rewrite. The server is
      // the truth either way, which is what the invalidation goes and gets.
      const stored = loadDoor();
      if (stored) saveDoorRules(stored, rules);
      void queryClient.invalidateQueries({ queryKey: qk.rulesCurrent() });
      // FR-11 — AND THE SHELF ABOVE THE CONVERSATION. A landed rules edit is
      // the one moment the server marks `edit_rules` done (`rules/edit.ts`), so
      // the doorman's thread — which is where the standing prompts come from —
      // is stale the instant this returns. Without this the CTA kept asking for
      // a job the owner had just finished, until a reload (owner, 2026-08-26).
      void queryClient.invalidateQueries({ queryKey: qk.doormanThread() });
    },
    [queryClient],
  );

  if (!seed) {
    return (
      <div>
        <h2 className="text-[12px] uppercase tracking-[0.18em] text-faint">{RULES_HEADING}</h2>
        <p className="mt-4 text-sm text-faint" role={isError ? "alert" : undefined}>
          {isError ? RULES_UNREACHABLE : isPending ? RULES_LOADING : RULES_UNREACHABLE}
        </p>
      </div>
    );
  }

  return (
    <RulesArea
      initial={seed}
      handle={me?.xHandle ?? null}
      /**
       * ADJUST, ON A CARD IN "NOB HAS NOTICED" (ruling R-0915-1 (4)). It used to
       * open this very editor on the proposed document; adjusting is talking
       * now, so the panel closes and the owner's own conversation comes up with
       * the composer pointed at the card. The editor stays where "Edit myself"
       * leaves it — in Settings, for the owner who wants it.
       */
      onAdjustProposal={ui.adjustProposal}
      onRulesChanged={onRulesChanged}
      onSessionExpired={sessionExpired}
      /**
       * "TELL NOB" DID NOTHING ON FREE (owner item 8, 2026-08-27).
       *
       * It closed the panel and put the ask in the composer — a composer a Free
       * owner cannot send from, because Free does no AI work at all (ruling
       * C10, R-0825-4). So the one button on this page that offers to do the
       * work FOR somebody dropped exactly the people who most needed it into a
       * locked box, with the sentence they wanted to say already typed in it.
       *
       * On Free it opens the §5.4 paywall instead — its one opener now. That
       * is the offer this refusal deserves: the doorman is what a plan buys,
       * and this is the moment somebody asked for the doorman. `Edit myself`
       * beside it is untouched and still free — nothing about the rules needs a
       * plan, only having Nob write them for you does.
       */
      onTellNob={() => {
        if (metered === false) return ui.openPaywall();
        ui.closeOverlay();
        ui.askDoorman(TELL_NOB_ASK);
      }}
    />
  );
}
